import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  AccountCard,
  Button,
  Card,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  LabelPill,
  PrimaryFullScreenSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from "@repo/ui";
import { NotFoundError } from "@repo/utils";
import { createFileRoute, useParams } from "@tanstack/react-router";
import Decimal from "decimal.js";
import { Suspense, useState } from "react";
import { DetailRow } from "~/components/DetailRow/DetailRow";
import { graphql } from "~/gql/client";
import { currencyValues } from "~/gql/client/enumValues";
import type { AdminCurrencycloudFundingAccountFragment } from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/currencycloud_/$accountId")({
  head: () => opsHead("Currencycloud Account"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { accountId } = useParams({ strict: false });
  if (!accountId) {
    throw new NotFoundError();
  }

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminCurrencycloudAccount($id: ID!) {
        currencycloudAccount(id: $id) {
          ...AdminCurrencycloudAccountDetails
        }
      }

      fragment AdminCurrencycloudAccountDetails on CurrencycloudAccount {
        id
        isHouseAccount
        accountName
        status
        country
        legalEntityType
        spreadTable
        settlementType
        shortReference
        apiTrading
        onlineTrading
        bankAccountVerified
        createdAt
        updatedAt
        balances {
          id
          currency
          amount
          asset {
            id
            ...AssetCardFields
          }
          createdAt
          updatedAt
        }
        contacts {
          id
          createdAt
          updatedAt
          firstName
          lastName
          enabled
          loginId
        }
        fundingAccounts(currencies: [AUD, USD]) {
          ...AdminCurrencycloudFundingAccount
        }
        beneficiaries {
          id
          name
          externalReference
          currency
          paymentTypes
          bankCountry
          bankName
          bankAddress
          bankAccountType
          bankAccountHolderName
          accountNumber
          iban
          bicSwift
          routingCodeType1
          routingCodeValue1
          routingCodeType2
          routingCodeValue2
          defaultBeneficiary
          beneficiaryEntityType
          beneficiaryCompanyName
          beneficiaryFirstName
          beneficiaryLastName
          beneficiaryAddress
          beneficiaryCity
          beneficiaryPostcode
          beneficiaryStateOrProvince
          beneficiaryCountry
          email
          createdAt
          updatedAt
        }
      }
    `),
    {
      variables: {
        id: accountId,
      },
    },
  );

  const [refreshCurrencycloudAccount] = useMutation(
    graphql(`
      mutation RefreshCurrencycloudAccount($input: RefreshCurrencycloudAccountInput!) {
        refreshCurrencycloudAccount(input: $input) {
          currencycloudAccount {
            id
          }
        }
      }
    `),
  );

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-row justify-between gap-4">
        <div className="font-semibold text-xl">Currencycloud Account</div>
        {!data.currencycloudAccount?.isHouseAccount && (
          <div className="flex gap-4">
            <Button
              onClick={() =>
                refreshCurrencycloudAccount({
                  variables: {
                    input: {
                      currencycloudAccountId: accountId,
                    },
                  },
                })
              }
            >
              Sync
            </Button>

            <EmulateInboundFundsDialog currencycloudAccountId={accountId} />
          </div>
        )}
      </div>
      <Card className="p-4">
        <DetailRow
          label="ID"
          value={
            <span className="font-mono">{data.currencycloudAccount?.id}</span>
          }
        />
        <DetailRow
          label="Account name"
          value={data.currencycloudAccount?.accountName}
        />
        <DetailRow
          label="Short reference"
          value={data.currencycloudAccount?.shortReference}
        />
        <DetailRow label="Country" value={data.currencycloudAccount?.country} />
        <DetailRow label="Status" value={data.currencycloudAccount?.status} />
        <DetailRow
          label="Legal entity type"
          value={data.currencycloudAccount?.legalEntityType}
        />
        <DetailRow
          label="Spread table"
          value={data.currencycloudAccount?.spreadTable}
        />
        <DetailRow
          label="Settlement type"
          value={data.currencycloudAccount?.settlementType}
        />
        <DetailRow
          label="Bank account verified"
          value={data.currencycloudAccount?.bankAccountVerified}
        />
        <DetailRow
          label="Trading status"
          value={
            <div className="flex gap-2">
              <LabelPill
                label="API trading"
                variant={
                  data.currencycloudAccount?.apiTrading ? "success" : "error"
                }
              />
              <LabelPill
                label="Online trading"
                variant={
                  data.currencycloudAccount?.onlineTrading ? "success" : "error"
                }
              />
            </div>
          }
        />
        <DetailRow
          label="Created"
          value={
            data.currencycloudAccount?.createdAt ? (
              <DateUI
                date={data.currencycloudAccount?.createdAt}
                variant="DATETIME_TZ"
              />
            ) : (
              "-"
            )
          }
        />
        <DetailRow
          label="Last Updated"
          value={
            data.currencycloudAccount?.updatedAt ? (
              <DateUI
                date={data.currencycloudAccount?.updatedAt}
                variant="DATETIME_TZ"
              />
            ) : (
              "-"
            )
          }
        />
      </Card>

      <div className="font-semibold text-xl">Balances</div>

      <DataTable
        data={data.currencycloudAccount?.balances ?? []}
        linkTo={(row) =>
          `/currencycloud/${accountId}/balances/${row.original.id}`
        }
        columns={[
          {
            accessorKey: "amount",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Amount" />
            ),
            cell: ({ row }) => (
              <CurrencyAmountUI
                amount={row.original.amount}
                currency={row.original.currency}
                showFlag
              />
            ),
          },
          {
            accessorKey: "asset",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Asset" />
            ),
            cell: ({ row }) => {
              if (row.original.asset) {
                return (
                  <AccountCard
                    account={row.original.asset}
                    subText="PROVIDER"
                  />
                );
              }
              return "Not linked";
            },
          },
          {
            accessorKey: "createdAt",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Created" />
            ),
            cell: ({ row }) => (
              <DateUI date={row.original.createdAt} variant="DATETIME_TZ" />
            ),
          },
          {
            accessorKey: "updatedAt",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Last updated" />
            ),
            cell: ({ row }) => (
              <DateUI date={row.original.updatedAt} variant="DATETIME_TZ" />
            ),
          },
        ]}
        className="overflow-hidden border"
        hidePagination={true}
        pageSize={10_000}
      />

      <div className="font-semibold text-xl">Contacts</div>

      <DataTable
        data={data.currencycloudAccount?.contacts ?? []}
        columns={[
          {
            accessorKey: "id",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="ID" />
            ),
            cell: ({ row }) => (
              <span className="font-mono">{row.original.id}</span>
            ),
          },
          {
            id: "name",
            accessorFn: (row) => `${row.firstName} ${row.lastName}`,
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Name" />
            ),
          },
          {
            accessorKey: "enabled",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Enabled" />
            ),
          },
          {
            accessorKey: "loginId",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Login ID" />
            ),
          },
          {
            accessorKey: "createdAt",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Created" />
            ),
            cell: ({ row }) => <DateUI date={row.original.createdAt} />,
          },
          {
            accessorKey: "updatedAt",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Last updated" />
            ),
            cell: ({ row }) => <DateUI date={row.original.updatedAt} />,
          },
        ]}
        className="overflow-hidden border"
        hidePagination={true}
        pageSize={10_000}
      />

      <FundingAccountsSection
        fundingAccounts={data.currencycloudAccount?.fundingAccounts ?? []}
      />

      <div className="font-semibold text-xl">Beneficiaries</div>

      <DataTable
        data={data.currencycloudAccount?.beneficiaries ?? []}
        columns={[
          {
            accessorKey: "id",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="id" />
            ),
            cell: ({ row }) => row.original.id ?? "null",
          },
          {
            accessorKey: "name",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="name" />
            ),
            cell: ({ row }) => row.original.name ?? "null",
          },
          {
            accessorKey: "externalReference",
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title="beneficiary_external_reference"
              />
            ),
            cell: ({ row }) => row.original.externalReference ?? "null",
          },
          {
            accessorKey: "currency",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="currency" />
            ),
            cell: ({ row }) => row.original.currency ?? "null",
          },
          {
            accessorKey: "paymentTypes",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="payment_types" />
            ),
            cell: ({ row }) => (row.original.paymentTypes ?? []).join(" | "),
          },
          {
            accessorKey: "bankCountry",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="bank_country" />
            ),
            cell: ({ row }) => row.original.bankCountry ?? "null",
          },
          {
            accessorKey: "bankName",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="bank_name" />
            ),
            cell: ({ row }) => row.original.bankName ?? "null",
          },
          {
            accessorKey: "bankAddress",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="bank_address" />
            ),
            cell: ({ row }) => (row.original.bankAddress ?? []).join(" | "),
          },
          {
            accessorKey: "bankAccountType",
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title="bank_account_type"
              />
            ),
            cell: ({ row }) => row.original.bankAccountType ?? "null",
          },
          {
            accessorKey: "bankAccountHolderName",
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title="bank_account_holder_name"
              />
            ),
            cell: ({ row }) => row.original.bankAccountHolderName ?? "null",
          },
          {
            accessorKey: "accountNumber",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="account_number" />
            ),
            cell: ({ row }) => row.original.accountNumber ?? "null",
          },
          {
            accessorKey: "iban",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="iban" />
            ),
            cell: ({ row }) => row.original.iban ?? "null",
          },
          {
            accessorKey: "bicSwift",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="bic_swift" />
            ),
            cell: ({ row }) => row.original.bicSwift ?? "null",
          },
          {
            accessorKey: "routingCodeType1",
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title="routing_code_type_1"
              />
            ),
            cell: ({ row }) => row.original.routingCodeType1 ?? "null",
          },
          {
            accessorKey: "routingCodeValue1",
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title="routing_code_value_1"
              />
            ),
            cell: ({ row }) => row.original.routingCodeValue1 ?? "null",
          },
          {
            accessorKey: "routingCodeType2",
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title="routing_code_type_2"
              />
            ),
            cell: ({ row }) => row.original.routingCodeType2 ?? "null",
          },
          {
            accessorKey: "routingCodeValue2",
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title="routing_code_value_2"
              />
            ),
            cell: ({ row }) => row.original.routingCodeValue2 ?? "null",
          },
          {
            accessorKey: "defaultBeneficiary",
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title="default_beneficiary"
              />
            ),
            cell: ({ row }) => String(row.original.defaultBeneficiary),
          },
          {
            accessorKey: "beneficiaryEntityType",
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title="beneficiary_entity_type"
              />
            ),
            cell: ({ row }) => row.original.beneficiaryEntityType ?? "null",
          },
          {
            accessorKey: "beneficiaryCompanyName",
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title="beneficiary_company_name"
              />
            ),
            cell: ({ row }) => row.original.beneficiaryCompanyName ?? "null",
          },
          {
            accessorKey: "beneficiaryFirstName",
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title="beneficiary_first_name"
              />
            ),
            cell: ({ row }) => row.original.beneficiaryFirstName ?? "null",
          },
          {
            accessorKey: "beneficiaryLastName",
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title="beneficiary_last_name"
              />
            ),
            cell: ({ row }) => row.original.beneficiaryLastName ?? "null",
          },
          {
            accessorKey: "beneficiaryAddress",
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title="beneficiary_address"
              />
            ),
            cell: ({ row }) =>
              (row.original.beneficiaryAddress ?? []).join(" | "),
          },
          {
            accessorKey: "beneficiaryCity",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="beneficiary_city" />
            ),
            cell: ({ row }) => row.original.beneficiaryCity ?? "null",
          },
          {
            accessorKey: "beneficiaryPostcode",
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title="beneficiary_postcode"
              />
            ),
            cell: ({ row }) => row.original.beneficiaryPostcode ?? "null",
          },
          {
            accessorKey: "beneficiaryStateOrProvince",
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title="beneficiary_state_or_province"
              />
            ),
            cell: ({ row }) =>
              row.original.beneficiaryStateOrProvince ?? "null",
          },
          {
            accessorKey: "beneficiaryCountry",
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title="beneficiary_country"
              />
            ),
            cell: ({ row }) => row.original.beneficiaryCountry ?? "null",
          },
          {
            accessorKey: "email",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="email" />
            ),
            cell: ({ row }) => row.original.email ?? "null",
          },
          {
            accessorKey: "createdAt",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="created_at" />
            ),
            cell: ({ row }) =>
              row.original.createdAt ? String(row.original.createdAt) : "null",
          },
          {
            accessorKey: "updatedAt",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="updated_at" />
            ),
            cell: ({ row }) =>
              row.original.updatedAt ? String(row.original.updatedAt) : "null",
          },
        ]}
        className="overflow-x-auto whitespace-nowrap border"
        hidePagination={true}
        pageSize={10_000}
      />
    </div>
  );
}

graphql(`
  fragment AdminCurrencycloudFundingAccount on CurrencycloudFundingAccount {
    id
    rail
    accountNumber
    accountNumberType
    accountHolderName
    bankName
    bankAddress
    bankCountry
    currency
    paymentType
    routingCode
    routingCodeType
    createdAt
    updatedAt
  }
`);

function FundingAccountsSection({
  fundingAccounts,
}: {
  fundingAccounts: AdminCurrencycloudFundingAccountFragment[];
}) {
  const fundingAccountCurrencies = Array.from(
    new Set(fundingAccounts.map((a) => a.currency)),
  );
  return (
    <>
      <div className="font-semibold text-xl">Funding Accounts</div>
      <Tabs defaultValue={fundingAccountCurrencies[0]}>
        <TabsList size="sm" className="mb-6">
          {fundingAccountCurrencies.map((currency) => (
            <TabsTrigger key={currency} value={currency}>
              {currency}
            </TabsTrigger>
          ))}
        </TabsList>
        {fundingAccountCurrencies.map((currency) => {
          const accountsForCurrency = fundingAccounts.filter(
            (fundingAccount) => fundingAccount.currency === currency,
          );
          return (
            <TabsContent key={currency} value={currency}>
              {accountsForCurrency.length === 0 ? (
                <div className="text-sm text-tertiary">
                  No {currency} funding accounts for this account.
                </div>
              ) : (
                <div className="flex flex-row flex-wrap items-start gap-4">
                  {accountsForCurrency.map((fundingAccount) => (
                    <FundingAccountCard
                      key={fundingAccount.id}
                      fundingAccount={fundingAccount}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          );
        })}
      </Tabs>
    </>
  );
}

const fundingAccountRailLabel: Record<
  AdminCurrencycloudFundingAccountFragment["rail"],
  string
> = {
  SWIFT: "SWIFT",
  IBAN: "IBAN",
  ACH: "ACH",
  FEDWIRE: "Fedwire",
  OTHER: "Other",
};

function FundingAccountRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <>
      <span className="font-title text-tertiary text-xs">{label}</span>
      <span className="break-words text-end text-sm">{value}</span>
    </>
  );
}

function FundingAccountCard({
  fundingAccount,
}: {
  fundingAccount: AdminCurrencycloudFundingAccountFragment;
}) {
  const accountNumberLabel =
    fundingAccount.accountNumberType === "iban" ? "IBAN" : "Account number";

  const routingCodeLabel = (() => {
    switch (fundingAccount.routingCodeType) {
      case "bic_swift":
        return "BIC / SWIFT";
      case "ach_routing_number":
        return "ACH routing number";
      case "wire_routing_number":
        return "Wire routing number";
      case "sort_code":
        return "Sort code";
      default:
        return "Routing code";
    }
  })();

  return (
    <Card className="w-80 p-4">
      <div className="mb-3 flex flex-row items-center justify-between gap-4">
        <span className="font-semibold">
          {fundingAccountRailLabel[fundingAccount.rail]}
        </span>
        <span className="text-sm text-tertiary">
          {fundingAccount.currency} · {fundingAccount.paymentType}
        </span>
      </div>
      <div className="grid grid-cols-[auto_1fr] items-baseline gap-x-2 gap-y-2 text-primary text-sm">
        <FundingAccountRow
          label="Account holder"
          value={fundingAccount.accountHolderName}
        />
        <FundingAccountRow
          label={accountNumberLabel}
          value={fundingAccount.accountNumber}
        />
        <FundingAccountRow
          label={routingCodeLabel}
          value={fundingAccount.routingCode}
        />
        <FundingAccountRow label="Bank name" value={fundingAccount.bankName} />
        <FundingAccountRow
          label="Bank address"
          value={fundingAccount.bankAddress}
        />
        <FundingAccountRow
          label="Bank country"
          value={fundingAccount.bankCountry}
        />
        <FundingAccountRow
          label="Last updated"
          value={
            <DateUI date={fundingAccount.updatedAt} variant="DATETIME_TZ" />
          }
        />
      </div>
    </Card>
  );
}

function EmulateInboundFundsDialog({
  currencycloudAccountId,
}: {
  currencycloudAccountId: string;
}) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState<string>("");
  const [reference, setReference] = useState<string>("");

  const [emulateInboundFunds, { loading: isSubmitting }] = useMutation(
    graphql(`
      mutation EmulateInboundFunds($input: CurrencycloudEmulateInboundFundsInput!) {
        currencycloudEmulateInboundFunds(input: $input) {
          currencycloudAccount {
            id
          }
        }
      }
    `),
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!amount || !currency || isSubmitting) {
      return;
    }

    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount) || numericAmount <= 0) {
      return;
    }

    try {
      await emulateInboundFunds({
        variables: {
          input: {
            currencycloudAccountId,
            amount: new Decimal(numericAmount),
            currency: currency as any,
            reference: reference !== "" ? reference : undefined,
          },
        },
      });

      setOpen(false);
      setAmount("");
      setCurrency("");
      toast({
        kind: "success",
        autoHide: true,
        title: "Inbound funds emulated successfully",
      });
    } catch (error) {
      toast({
        kind: "error",
        autoHide: false,
        title: "Failed to emulate inbound funds",
        description: `Error: ${error}`,
      });
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setAmount("");
      setCurrency("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>Emulate inbound funds</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Emulate Inbound Funds</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="amount">Amount</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="0"
              placeholder="Enter amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="currency">Currency</Label>
            <Select value={currency} onValueChange={setCurrency} required>
              <SelectTrigger>
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent>
                {currencyValues.map((curr) => (
                  <SelectItem key={curr} value={curr}>
                    {curr}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="currency">Reference</Label>
            <Input
              type="text"
              name="reference"
              placeholder=""
              onChange={(event) => {
                setReference(event.target.value);
              }}
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary" disabled={isSubmitting}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant="primary"
              type="submit"
              loading={isSubmitting}
              disabled={!amount || !currency || isSubmitting}
            >
              {isSubmitting ? "Processing..." : "Emulate Funds"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
